import { useEffect } from "react";

const colors = {
  ink: "#0e0e0e",
  paper: "#f5f0e8",
  warm: "#f0e6d0",
  ember: "#c94a1a",
  earth: "#7a4f2b",
  moss: "#3a5a3c",
  sky: "#1a3a5c",
  gold: "#c8922a",
  cream: "#faf7f2",
};

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }

  body {
    background-color: ${colors.paper};
    color: ${colors.ink};
    font-family: 'DM Sans', sans-serif;
    font-weight: 300;
    overflow-x: hidden;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(28px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @keyframes slowSpin {
    from { transform: translate(-50%,-50%) rotate(0deg); }
    to   { transform: translate(-50%,-50%) rotate(360deg); }
  }

  .ezems-noise-overlay {
    position: fixed; inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
    pointer-events: none; z-index: 1000; opacity: 0.4;
  }

  /* HERO */
  .hero { min-height: 100vh; display: grid; grid-template-columns: 55% 45%; position: relative; overflow: hidden; }
  .hero-left {
    background-color: ${colors.sky};
    padding: 80px 64px 80px 60px;
    display: flex; flex-direction: column; justify-content: center;
    position: relative; z-index: 2;
  }
  .hero-left::after {
    content: '';
    position: absolute; right: -55px; top: 0; bottom: 0; width: 110px;
    background-color: ${colors.sky};
    clip-path: polygon(0 0, 50% 0, 100% 50%, 50% 100%, 0 100%);
    z-index: 3;
  }
  .hero-right {
    background-color: ${colors.ember};
    position: relative; overflow: hidden;
    display: flex; align-items: flex-end; justify-content: flex-end;
    padding: 60px;
  }
  .hero-right::before {
    content: '🌍';
    position: absolute; font-size: 420px; opacity: 0.07;
    top: 50%; left: 50%; transform: translate(-50%, -50%);
    line-height: 1; animation: slowSpin 60s linear infinite;
  }
  .hero-right-content { position: relative; z-index: 2; text-align: right; }
  .hero-right-content blockquote {
    font-family: 'Playfair Display', serif;
    font-size: clamp(16px, 2vw, 24px);
    font-style: italic; color: ${colors.cream};
    line-height: 1.5; margin-bottom: 16px; max-width: 280px;
  }
  .hero-right-content cite {
    font-family: 'DM Mono', monospace; font-size: 10px;
    letter-spacing: 3px; color: rgba(250,247,242,0.4);
    text-transform: uppercase; font-style: normal;
  }
  .overline {
    font-family: 'DM Mono', monospace; font-size: 10px;
    letter-spacing: 4px; text-transform: uppercase;
    color: ${colors.gold}; margin-bottom: 28px;
    opacity: 0; animation: fadeUp 0.7s ease forwards 0.2s;
  }
  .hero-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(54px, 7vw, 96px);
    font-weight: 900; line-height: 0.9; color: ${colors.cream};
    opacity: 0; animation: fadeUp 0.8s ease forwards 0.4s;
  }
  .hero-title em { font-style: italic; color: ${colors.gold}; }
  .hero-sub {
    margin-top: 36px; font-size: 15px; font-weight: 300;
    color: rgba(250,247,242,0.65); max-width: 360px; line-height: 1.75;
    opacity: 0; animation: fadeUp 0.8s ease forwards 0.65s;
  }
  .hero-cta {
    margin-top: 44px; display: flex; gap: 16px; flex-wrap: wrap;
    opacity: 0; animation: fadeUp 0.8s ease forwards 0.85s;
  }

  /* BUTTONS */
  .btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 14px 28px;
    font-family: 'DM Mono', monospace; font-size: 11px;
    letter-spacing: 2px; text-transform: uppercase;
    text-decoration: none; border-radius: 2px;
    transition: all 0.25s ease; cursor: pointer; border: none;
  }
  .btn-primary { background: ${colors.gold}; color: ${colors.ink}; }
  .btn-primary:hover { background: #d9a33a; transform: translateY(-2px); }
  .btn-outline { background: transparent; color: ${colors.cream}; border: 1px solid rgba(250,247,242,0.3); }
  .btn-outline:hover { border-color: ${colors.cream}; background: rgba(250,247,242,0.05); }
  .btn-lg { padding: 18px 40px; font-size: 12px; }
  .btn-warm { background: ${colors.warm}; color: ${colors.ink}; border: 1px solid rgba(0,0,0,0.15); }
  .btn-warm:hover { background: #e8dcc8; transform: translateY(-2px); }

  /* LAYOUT */
  .container { max-width: 1100px; margin: 0 auto; padding: 0 60px; }
  .section { padding: 100px 0; }

  .section-label {
    font-family: 'DM Mono', monospace; font-size: 10px;
    letter-spacing: 4px; text-transform: uppercase;
    color: ${colors.ember}; margin-bottom: 56px;
    display: flex; align-items: center; gap: 16px;
  }
  .section-label::after {
    content: ''; flex: 1; height: 1px;
    background: ${colors.ember}; opacity: 0.25; max-width: 80px;
  }
  .section-label-dim { color: rgba(250,247,242,0.3); }
  .section-label-dim::after { background: rgba(250,247,242,0.3); }

  /* NARRATIVE */
  .narrative { background: ${colors.cream}; }
  .narrative-grid { display: grid; grid-template-columns: 1fr 2fr; gap: 80px; align-items: start; }
  .narrative-anchor { position: sticky; top: 80px; }
  .big-label { font-family: 'Playfair Display', serif; font-size: clamp(40px, 5vw, 64px); font-weight: 900; line-height: 1.0; color: ${colors.sky}; }
  .big-label .line2 { display: block; font-style: italic; color: ${colors.ember}; }
  .narrative-body p { font-size: 17px; line-height: 1.9; color: #333; margin-bottom: 22px; }
  .narrative-body p strong { color: ${colors.sky}; font-weight: 500; }
  .highlight-line {
    border-left: 3px solid ${colors.ember};
    padding: 16px 24px; margin: 32px 0;
    background: ${colors.warm};
    font-family: 'Playfair Display', serif;
    font-size: 20px; font-style: italic;
    color: ${colors.earth}; line-height: 1.5;
  }

  /* PROBLEMS */
  .problems { background: ${colors.ink}; padding: 80px 0; }
  .problems-header { margin-bottom: 48px; }
  .problems-header h2 { font-family: 'Playfair Display', serif; font-size: clamp(28px, 3.5vw, 44px); color: ${colors.cream}; line-height: 1.2; }
  .problems-header h2 em { color: ${colors.gold}; font-style: italic; }
  .problems-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: rgba(255,255,255,0.08); }
  .problem-card { background: ${colors.ink}; padding: 44px 32px; position: relative; overflow: hidden; transition: background 0.3s; }
  .problem-card:hover { background: #181818; }
  .problem-card-num { position: absolute; top: 18px; right: 20px; font-family: 'DM Mono', monospace; font-size: 10px; color: rgba(255,255,255,0.12); letter-spacing: 2px; }
  .p-icon { font-size: 30px; margin-bottom: 18px; display: block; }
  .p-title { font-family: 'Playfair Display', serif; font-size: 17px; color: ${colors.cream}; margin-bottom: 10px; }
  .p-desc { font-size: 13px; color: rgba(255,255,255,0.38); line-height: 1.65; }

  /* QUOTE */
  .quote-band { background: ${colors.earth}; padding: 100px 0; position: relative; overflow: hidden; }
  .quote-band::before {
    content: '"';
    position: absolute; font-family: 'Playfair Display', serif;
    font-size: 700px; color: rgba(255,255,255,0.04);
    top: -150px; left: -40px; line-height: 1; pointer-events: none;
  }
  .quote-center { max-width: 820px; margin: 0 auto; padding: 0 60px; text-align: center; }
  .pull-quote { font-family: 'Playfair Display', serif; font-size: clamp(28px, 4vw, 52px); font-style: italic; color: ${colors.cream}; line-height: 1.3; margin-bottom: 28px; }
  .pull-quote em { color: ${colors.gold}; font-style: normal; }
  .pull-attr { font-family: 'DM Mono', monospace; font-size: 10px; letter-spacing: 3px; text-transform: uppercase; color: rgba(250,247,242,0.35); }

  /* SOLUTION */
  .solution { background: ${colors.paper}; }
  .solution-intro { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; margin-bottom: 60px; }
  .solution-intro h2 { font-family: 'Playfair Display', serif; font-size: clamp(32px, 3.5vw, 48px); font-weight: 700; line-height: 1.15; color: ${colors.sky}; }
  .solution-intro h2 span { display: block; font-style: italic; color: ${colors.ember}; }
  .solution-intro p { font-size: 16px; line-height: 1.85; color: #444; margin-bottom: 18px; }
  .platform-link {
    display: inline-flex; align-items: center; gap: 8px;
    font-family: 'DM Mono', monospace; font-size: 11px; letter-spacing: 2px;
    text-transform: uppercase; color: ${colors.ember}; text-decoration: none;
    border-bottom: 1px solid rgba(201,74,26,0.3); padding-bottom: 4px;
    transition: border-color 0.2s, gap 0.2s;
  }
  .platform-link:hover { border-color: ${colors.ember}; gap: 12px; }
  .features-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 2px; background: rgba(0,0,0,0.07); }
  .feature-card { background: ${colors.cream}; padding: 44px 40px; display: flex; gap: 20px; align-items: flex-start; transition: background 0.3s; }
  .feature-card:hover { background: ${colors.warm}; }
  .f-dot { width: 9px; height: 9px; border-radius: 50%; margin-top: 7px; flex-shrink: 0; }
  .f-text h3 { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 700; color: ${colors.sky}; margin-bottom: 8px; }
  .f-text p { font-size: 14px; color: #555; line-height: 1.7; }

  /* EQUITY */
  .equity-band { background: ${colors.moss}; padding: 88px 0; position: relative; overflow: hidden; }
  .equity-band::after {
    content: 'EQUITY';
    position: absolute; font-family: 'Playfair Display', serif;
    font-size: 220px; font-weight: 900; color: rgba(255,255,255,0.04);
    top: 50%; left: 50%; transform: translate(-50%,-50%);
    white-space: nowrap; letter-spacing: 24px; pointer-events: none;
  }
  .equity-inner { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; position: relative; z-index: 2; }
  .equity-text h2 { font-family: 'Playfair Display', serif; font-size: clamp(32px, 3.5vw, 48px); color: ${colors.cream}; line-height: 1.15; margin-bottom: 24px; }
  .equity-text h2 em { color: ${colors.gold}; font-style: italic; }
  .equity-text p { font-size: 15px; line-height: 1.85; color: rgba(250,247,242,0.65); margin-bottom: 16px; }
  .equity-pillars { display: flex; flex-direction: column; gap: 2px; }
  .pillar { background: rgba(255,255,255,0.05); padding: 28px 32px; display: flex; align-items: center; gap: 20px; transition: background 0.3s; }
  .pillar:hover { background: rgba(255,255,255,0.09); }
  .pillar-icon { font-size: 28px; flex-shrink: 0; }
  .pillar-text h4 { font-family: 'Playfair Display', serif; font-size: 17px; color: ${colors.cream}; margin-bottom: 4px; }
  .pillar-text p { font-size: 13px; color: rgba(250,247,242,0.45); line-height: 1.5; }

  /* VISION */
  .vision { background: ${colors.sky}; padding: 120px 0; position: relative; overflow: hidden; }
  .vision::before { content: ''; position: absolute; width: 700px; height: 700px; border-radius: 50%; border: 1px solid rgba(255,255,255,0.05); top: -250px; right: -180px; }
  .vision-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 100px; align-items: center; }
  .vision h2 { font-family: 'Playfair Display', serif; font-size: clamp(34px, 4vw, 52px); color: ${colors.cream}; line-height: 1.1; margin-bottom: 28px; }
  .vision h2 em { color: ${colors.gold}; font-style: italic; }
  .vision p { font-size: 15px; line-height: 1.85; color: rgba(250,247,242,0.6); margin-bottom: 18px; }
  .roadmap { padding-left: 28px; position: relative; }
  .roadmap::before { content: ''; position: absolute; left: 0; top: 10px; bottom: 10px; width: 1px; background: linear-gradient(to bottom, ${colors.gold}, transparent); }
  .rm-item { padding: 20px 0 20px 28px; border-bottom: 1px solid rgba(255,255,255,0.07); position: relative; }
  .rm-item:last-child { border-bottom: none; }
  .rm-item::before { content: ''; position: absolute; left: -5px; top: 26px; width: 10px; height: 10px; border-radius: 50%; background: ${colors.gold}; }
  .rm-step { font-family: 'DM Mono', monospace; font-size: 10px; letter-spacing: 3px; color: ${colors.gold}; text-transform: uppercase; margin-bottom: 6px; }
  .rm-item h3 { font-family: 'Playfair Display', serif; font-size: 22px; color: ${colors.cream}; margin-bottom: 4px; }
  .rm-item p { font-size: 13px; color: rgba(250,247,242,0.38); line-height: 1.6; }

  /* CTA */
  .cta-section { background: ${colors.paper}; padding: 120px 0; text-align: center; }
  .cta-section h2 { font-family: 'Playfair Display', serif; font-size: clamp(36px, 5vw, 64px); font-weight: 900; color: ${colors.sky}; line-height: 1.05; margin-bottom: 20px; }
  .cta-section h2 em { color: ${colors.ember}; font-style: italic; }
  .cta-section > .container > p { font-size: 17px; color: #555; max-width: 560px; margin: 0 auto 48px; line-height: 1.8; }
  .cta-buttons { display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; margin-bottom: 56px; }
  .hashtags { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; }
  .tag { font-family: 'DM Mono', monospace; font-size: 10px; letter-spacing: 1.5px; color: ${colors.earth}; border: 1px solid rgba(122,79,43,0.25); padding: 6px 14px; border-radius: 2px; background: ${colors.warm}; text-decoration: none; transition: background 0.2s, border-color 0.2s, color 0.2s; }
  .tag:hover { background: ${colors.gold}; color: ${colors.cream}; border-color: ${colors.gold}; }

  /* CONTACT */
  .contact-bar { background: ${colors.ink}; }
  .contact-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: rgba(255,255,255,0.07); }
  .contact-card { background: ${colors.ink}; padding: 48px 36px; text-decoration: none; display: flex; flex-direction: column; gap: 12px; transition: background 0.3s; position: relative; overflow: hidden; }
  .contact-card:hover { background: #161616; }
  .contact-card::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 2px; transform: scaleX(0); transform-origin: left; transition: transform 0.35s ease; }
  .contact-card:hover::after { transform: scaleX(1); }
  .contact-card.whatsapp::after { background: #25D366; }
  .contact-card.phone::after { background: ${colors.ember}; }
  .contact-card.facebook::after { background: #1877F2; }
  .contact-card.linkedin::after { background: #0A66C2; }
  .contact-label { font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 3px; text-transform: uppercase; color: rgba(255,255,255,0.25); }
  .contact-icon { font-size: 24px; }
  .contact-value { font-family: 'Playfair Display', serif; font-size: 15px; color: ${colors.cream}; line-height: 1.4; }
  .contact-arrow { margin-top: auto; font-family: 'DM Mono', monospace; font-size: 18px; color: rgba(255,255,255,0.15); transition: color 0.3s, transform 0.3s; }
  .contact-card:hover .contact-arrow { color: ${colors.gold}; transform: translateX(4px); }

  /* FOOTER */
  footer { background: #070707; padding: 60px 0 36px; }
  .footer-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 48px; }
  .footer-brand { font-family: 'Playfair Display', serif; font-size: 30px; font-weight: 900; color: ${colors.cream}; }
  .footer-brand span { color: ${colors.ember}; }
  .footer-tagline { font-size: 13px; color: rgba(255,255,255,0.25); margin-top: 8px; max-width: 260px; line-height: 1.6; }
  .footer-quote { font-family: 'Playfair Display', serif; font-size: 18px; font-style: italic; color: ${colors.gold}; max-width: 340px; text-align: right; line-height: 1.5; }
  .footer-bottom { border-top: 1px solid rgba(255,255,255,0.06); padding-top: 24px; display: flex; justify-content: space-between; align-items: center; }
  .footer-bottom span { font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 2px; color: rgba(255,255,255,0.18); text-transform: uppercase; }

  /* SCROLL REVEAL */
  .reveal { opacity: 0; transform: translateY(36px); transition: opacity 0.75s ease, transform 0.75s ease; }
  .reveal.visible { opacity: 1; transform: translateY(0); }
  .reveal-delay-1 { transition-delay: 0.1s; }
  .reveal-delay-2 { transition-delay: 0.2s; }
  .reveal-delay-3 { transition-delay: 0.3s; }
  .reveal-delay-4 { transition-delay: 0.4s; }

  /* RESPONSIVE */
  @media (max-width: 900px) {
    .hero { grid-template-columns: 1fr; }
    .hero-left { padding: 80px 32px 60px; }
    .hero-left::after { display: none; }
    .hero-right { min-height: 260px; padding: 40px 32px; }
    .narrative-grid, .solution-intro, .equity-inner, .vision-grid { grid-template-columns: 1fr; gap: 44px; }
    .problems-grid, .contact-grid { grid-template-columns: 1fr 1fr; }
    .features-grid { grid-template-columns: 1fr; }
    .container { padding: 0 28px; }
    .section { padding: 72px 0; }
    .footer-top { flex-direction: column; gap: 32px; }
    .footer-quote { text-align: left; }
    .footer-bottom { flex-direction: column; gap: 12px; text-align: center; }
    .quote-center { padding: 0 28px; }
    .narrative-anchor { position: static; }
  }
  @media (max-width: 560px) {
    .problems-grid, .contact-grid { grid-template-columns: 1fr; }
    .cta-buttons { flex-direction: column; align-items: center; }
  }
`;

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

const problems = [
  { num: "01", icon: "📋", title: "Paper Files", desc: "Stacks of physical documentation, impossible to track or search across a school year." },
  { num: "02", icon: "✍️", title: "Manual Assessments", desc: "Hours of handwriting individual assessments with no systematic way to analyse results." },
  { num: "03", icon: "📊", title: "Disconnected Spreadsheets", desc: "Data scattered across files with no unified view of each learner's progress over time." },
  { num: "04", icon: "⏳", title: "Report Overload", desc: "Endless report writing that consumed the hours teachers needed for actual teaching." },
];

const features = [
  { dot: colors.ember, title: "Digital Competency Tracking", desc: "Teachers track individual learner competencies digitally — aligned directly to the CBE framework with zero extra setup." },
  { dot: colors.moss, title: "Automatic Report Generation", desc: "Reports generate automatically from recorded assessments — saving teachers hours every term." },
  { dot: colors.gold, title: "Real-Time Parent Access", desc: "Parents see their child's progress in real time — keeping families engaged and informed throughout the year." },
  { dot: colors.sky, title: "School Analytics & Clarity", desc: "Structured analytics give school administrators the data they need to make informed, evidence-based decisions." },
];

const pillars = [
  { icon: "👩‍🏫", title: "Teachers deserve support", desc: "Tools that work with them, not against them." },
  { icon: "👨‍👩‍👧", title: "Parents deserve transparency", desc: "Real visibility into their child's learning journey." },
  { icon: "🎓", title: "Learners deserve systems that work", desc: "Every child, in every school, in every region." },
];

const roadmap = [
  { step: "Today", place: "Kenya", desc: "Full CBE LMS implementation aligned to Kenya's national curriculum framework — built for every school, every learner." },
  { step: "Tomorrow", place: "East Africa", desc: "Scaling the platform to serve educators and learners across the region with locally-adapted tools." },
  { step: "The Future", place: "The Continent", desc: "Pan-African digital education infrastructure — owned, operated, and evolved by Africans." },
];

const contacts = [
  { cls: "whatsapp", href: "https://wa.me/254101086123", label: "WhatsApp", icon: "💬", value: "+254 101 086 123" },
  { cls: "phone", href: "tel:+254799086123", label: "Call Us", icon: "📞", value: "+254 799 086 123" },
  { cls: "facebook", href: "https://www.facebook.com/ezemstech", label: "Facebook", icon: "📘", value: "facebook.com/ezemstech" },
  { cls: "linkedin", href: "https://www.linkedin.com/in/ziroh-katana-24b23ba9/", label: "LinkedIn", icon: "💼", value: "Ziroh Katana" },
];

const hashtags = ["#EducationForAll", "#CBE", "#EdTechAfrica", "#FoundersJourney", "#AfricaInnovation", "#BuildInPublic"];

export default function EzemsStory() {
  useReveal();

  return (
    <>
      <style>{globalStyles}</style>
      <div className="ezems-noise-overlay" />

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-left">
          <div className="overline">Kenya · EdTech · CBE · Founded in Purpose</div>
          <h1 className="hero-title">
            EZEMS<br />Tech<br /><em>Developers</em>
          </h1>
          <p className="hero-sub">
            Building digital infrastructure for Competency-Based Education in Kenya — so teachers can teach, and learners can learn.
          </p>
          <div className="hero-cta">
            <a href="https://lms.ezems.co.ke" target="_blank" rel="noreferrer" className="btn btn-primary btn-lg">Visit the Platform →</a>
            <a href="https://wa.me/254101086123" target="_blank" rel="noreferrer" className="btn btn-outline btn-lg">WhatsApp Us</a>
          </div>
        </div>
        <div className="hero-right">
          <div className="hero-right-content">
            <blockquote>"The future of education in Africa must be built by Africans."</blockquote>
            <cite>Kenya · East Africa · The Continent</cite>
          </div>
        </div>
      </section>

      {/* ── NARRATIVE ── */}
      <section className="section narrative">
        <div className="container">
          <div className="section-label">The Story Behind EZEMS</div>
          <div className="narrative-grid">
            <div className="narrative-anchor reveal">
              <h2 className="big-label">
                A teacher.<br />A moment.<br /><span className="line2">A mission.</span>
              </h2>
            </div>
            <div className="narrative-body">
              <p className="reveal">A teacher once told me something that disturbed me. She looked tired. Not because she didn't love teaching. But because she was <strong>drowning in paperwork.</strong></p>
              <div className="highlight-line reveal reveal-delay-1">"The curriculum is good… but the paperwork is too much."</div>
              <p className="reveal reveal-delay-2">That moment changed me. When Kenya transitioned to Competency-Based Education, everyone talked about the vision — future-ready learners, skills over memorisation, creativity, critical thinking.</p>
              <p className="reveal reveal-delay-2">But inside classrooms? Teachers were staying late to manually track competencies. Filling endless assessment forms. Writing reports by hand. Managing disconnected spreadsheets.</p>
              <p className="reveal reveal-delay-3">CBE demands continuous assessment. But many schools — especially rural ones — were trying to deliver a 21st-century curriculum with <strong>20th-century tools.</strong></p>
              <p className="reveal reveal-delay-3">I kept asking myself: <strong>How can we expect teachers to inspire learners… when the system is exhausting them?</strong></p>
              <p className="reveal reveal-delay-4">I come from communities where resources are limited. I know what it means when systems fail quietly. It is always the learner who pays the price.</p>
              <p className="reveal reveal-delay-4" style={{ fontFamily: "'Playfair Display', serif", fontSize: "21px", fontStyle: "italic", color: colors.ember, marginTop: "12px" }}>
                If the infrastructure doesn't exist — build it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── PROBLEMS ── */}
      <section className="problems">
        <div className="container">
          <div className="problems-header reveal">
            <h2>Teachers were doing their best with <em>the wrong tools.</em></h2>
          </div>
        </div>
        <div className="problems-grid">
          {problems.map((p, i) => (
            <div className={`problem-card reveal${i > 0 ? ` reveal-delay-${i}` : ""}`} key={p.num}>
              <span className="problem-card-num">{p.num}</span>
              <span className="p-icon">{p.icon}</span>
              <h3 className="p-title">{p.title}</h3>
              <p className="p-desc">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── QUOTE ── */}
      <section className="quote-band">
        <div className="quote-center">
          <p className="pull-quote reveal">"The curriculum is good… but the <em>paperwork is too much.</em>"</p>
          <p className="pull-attr reveal reveal-delay-1">— A Kenyan Teacher, in conversation · The seed of EZEMS</p>
        </div>
      </section>

      {/* ── SOLUTION ── */}
      <section className="section solution">
        <div className="container">
          <div className="section-label">The EZEMS CBE LMS</div>
          <div className="solution-intro">
            <div className="reveal">
              <h2>Not just another platform. <span>Digital infrastructure for CBE.</span></h2>
            </div>
            <div className="reveal reveal-delay-2">
              <p>That is how EZEMS Tech Developers was born — and from it, the <strong style={{ color: colors.sky }}>EZEMS CBE Learning Management System.</strong> Built specifically for CBE schools in Kenya. Not generic. Not repurposed. Purpose-built.</p>
              <p style={{ fontStyle: "italic", color: colors.earth }}>Africa does not lack talent. We lack systems that support talent.</p>
              <br />
              <a href="https://lms.ezems.co.ke" target="_blank" rel="noreferrer" className="platform-link">Visit lms.ezems.co.ke →</a>
            </div>
          </div>
          <div className="features-grid">
            {features.map((f, i) => (
              <div className={`feature-card reveal${i > 0 ? ` reveal-delay-${i}` : ""}`} key={f.title}>
                <div className="f-dot" style={{ background: f.dot }} />
                <div className="f-text">
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EQUITY ── */}
      <section className="equity-band">
        <div className="container">
          <div className="equity-inner">
            <div className="equity-text reveal">
              <h2>This is bigger than software.<br />This is about <em>equity.</em></h2>
              <p>If rural schools cannot implement CBE effectively, the inequality gap will grow. The children who already have the least will fall even further behind.</p>
              <p><strong style={{ color: colors.cream }}>Technology should reduce inequality — not widen it.</strong></p>
              <p>EZEMS is a commitment to making sure that does not happen.</p>
            </div>
            <div className="equity-pillars reveal reveal-delay-2">
              {pillars.map((p) => (
                <div className="pillar" key={p.title}>
                  <span className="pillar-icon">{p.icon}</span>
                  <div className="pillar-text">
                    <h4>{p.title}</h4>
                    <p>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── VISION ── */}
      <section className="vision">
        <div className="container">
          <div className="vision-grid">
            <div className="reveal">
              <div className="section-label section-label-dim" style={{ marginBottom: "40px" }}>The Bigger Picture</div>
              <h2>Pan-African digital education infrastructure — <em>built by Africans.</em></h2>
              <p>EZEMS is not just about software. It is about building the digital backbone that African education systems need to thrive — starting with Kenya's CBE framework.</p>
              <p>Today we focus on Kenya. Tomorrow, East Africa. In the future, across the continent. Because the future of education in Africa must be built by Africans. And it must work for everyone — especially the underserved.</p>
            </div>
            <div className="roadmap reveal reveal-delay-2">
              {roadmap.map((r) => (
                <div className="rm-item" key={r.step}>
                  <div className="rm-step">{r.step}</div>
                  <h3>{r.place}</h3>
                  <p>{r.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section">
        <div className="container">
          <h2 className="reveal">If you believe education is the<br /><em>most powerful tool for change —</em></h2>
          <p className="reveal reveal-delay-1">But only when supported by the right systems. Let's build that future together. We're just getting started.</p>
          <div className="cta-buttons reveal reveal-delay-2">
            <a href="https://lms.ezems.co.ke" target="_blank" rel="noreferrer" className="btn btn-primary btn-lg">Explore the Platform</a>
            <a href="https://wa.me/254101086123" target="_blank" rel="noreferrer" className="btn btn-warm btn-lg">💬 WhatsApp</a>
            <a href="tel:+254799086123" className="btn btn-warm btn-lg">📞 Call Us</a>
          </div>
          <div className="hashtags reveal reveal-delay-3">
            {hashtags.map((tag) => (
              <span className="tag" key={tag}>{tag}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <div className="contact-bar">
        <div className="contact-grid">
          {contacts.map((c) => (
            <a href={c.href} target={c.href.startsWith("tel") ? "_self" : "_blank"} rel="noreferrer" className={`contact-card ${c.cls}`} key={c.cls}>
              <span className="contact-label">{c.label}</span>
              <span className="contact-icon">{c.icon}</span>
              <span className="contact-value">{c.value}</span>
              <span className="contact-arrow">→</span>
            </a>
          ))}
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer>
        <div className="container">
          <div className="footer-top">
            <div>
              <div className="footer-brand">EZEMS <span>Tech</span></div>
              <p className="footer-tagline">Digital infrastructure for CBE schools in Kenya — and beyond.</p>
            </div>
            <p className="footer-quote">"We're just getting started."</p>
          </div>
          <div className="footer-bottom">
            <span>EZEMS Tech Developers · Kenya</span>
            <span>lms.ezems.co.ke</span>
            <span>Built for Everyone · #EdTechAfrica</span>
          </div>
        </div>
      </footer>
    </>
  );
}